import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Grid, Card, CardHeader, CardContent, Stack, Button, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { toast } from 'react-toastify';

import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Iconify from '../../components/iconify';
import Loading from '../../layouts/loading';
import EmptyContent from '../../components/empty-content/empty-content';
import AppWidgetSummary from '../../components/card/AppWidgetSummary';
import { getStatusLabel } from './constants/assetConstants';
import { getAssetStats } from '../../services/assetService';

const PIE_COLORS = ['#00A76F', '#2196F3', '#FFAB00', '#919EAB', '#FF5630', '#8E33FF'];

// Format a number as Indian Rupees (e.g. ₹1,23,456.00).
const fRupee = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(value ?? 0);

// Convert an object map ({ key: count }) into the [{ name, value }] shape recharts wants.
const toChartData = (map, labelFn = (k) => k) =>
  Object.entries(map || {}).map(([key, value]) => ({ name: labelFn(key), value }));

export default function AssetDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getAssetStats();
      setStats(res?.data?.data ?? res?.data ?? null);
    } catch (error) {
      toast.error('Error loading asset stats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Assets', path: '/assets' },
    { label: 'Dashboard', path: '' },
  ];

  const statusData = toChartData(stats?.byStatus, getStatusLabel);
  const typeData = toChartData(stats?.byType);

  return (
    <>
      <Helmet>
        <title>Asset Dashboard | Logwork</title>
      </Helmet>

      <Loading open={isLoading} />

      <BreadCrumb breadcrumbs={breadcrumbs} />

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Button
            color="inherit"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/assets')}
          >
            Back to Assets
          </Button>
        </Stack>

        {stats && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary title="Total Assets" value={stats.total ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary title="Assigned" value={stats.assigned ?? 0} sx={{ bgcolor: 'info.lighter' }} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Total Revenue"
                value={fRupee(stats.totalValue ?? 0)}
                sx={{ bgcolor: 'success.lighter' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Warranties Expiring (30d)"
                value={stats.warrantyExpiringSoon ?? 0}
                sx={{ bgcolor: 'warning.lighter' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardHeader title="Assets by Status" />
                <CardContent sx={{ height: 320 }}>
                  {statusData.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={100} label>
                          {statusData.map((entry, index) => (
                            <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyContent title="No data" />
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardHeader title="Assets by Type" />
                <CardContent sx={{ height: 320 }}>
                  {typeData.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={typeData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#2196F3" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyContent title="No data" />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {!stats && !isLoading && (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            No statistics available.
          </Typography>
        )}
      </Container>
    </>
  );
}
